/**
 * Collection page support script.
 *
 * Responsibilities:
 *   - Initialize / observe per-card Swipers as cards enter the viewport
 *   - Maintain the counter and progress bar based on the server-rendered
 *     data attributes on #counter-display
 *
 * The actual page-to-page navigation (prefetch, swap, history) lives in the
 * pagination-collection.liquid snippet, so this script stays focused on
 * card-level UI lifecycle and counter math.
 */
document.addEventListener("DOMContentLoaded", function () {
  // ----------------------------
  // GUARDS
  // ----------------------------
  var getProductGrid = function () {
    return document.getElementById("product-grid");
  };
  // Also run on the search results page: it uses the same #product-grid,
  // card-wrapper, and inner-slider markup as the collection page but does not
  // have .collection-page-grid. facets.js on search also depends on
  // window.observeAllCards and window.__CollectionInitCardSwiper being defined.
  var isCollectionPage = !!document.querySelector(".collection-page-grid") ||
                         !!document.querySelector(".template-search__results");
  if (!getProductGrid() || !isCollectionPage) return;

  // ----------------------------
  // COUNTER
  // ----------------------------
  var counterEls = (function () {
    var el = document.getElementById("counter-display");
    if (!el) return null;
    return {
      root: el,
      bar: document.querySelector(".percentage_counter span"),
      start: document.getElementById("start-count"),
      end: document.getElementById("end-count"),
    };
  })();

  function updateCounter() {
    if (!counterEls || !counterEls.bar || !counterEls.start || !counterEls.end) return;

    var total = parseInt(counterEls.root.getAttribute("data-total-products"), 10) || 0;
    var offset = parseInt(counterEls.root.getAttribute("data-current-offset"), 10) || 0;
    var pageSize = parseInt(counterEls.root.getAttribute("data-page-size"), 10) || 0;

    counterEls.start.textContent = total > 0 ? offset + 1 : 0;
    counterEls.end.textContent = Math.min(offset + pageSize, total);

    var endNum = parseInt(counterEls.end.textContent, 10) || 0;
    var pct = total > 0 ? Math.round((endNum / total) * 100) : 0;
    counterEls.bar.style.width = Math.min(pct, 100) + "%";
  }

  updateCounter();

  // ----------------------------
  // PER-CARD SWIPER LIFECYCLE
  // ----------------------------
  var ACTIVE = new WeakMap();
  var swiperInitQueue = [];
  var swiperQueueRunning = false;

  function isDesktopHover() {
    return window.matchMedia(
      "(min-width: 990px) and (hover: hover) and (pointer: fine)"
    ).matches;
  }

  function getContainer(card) {
    return (
      card.querySelector(".slide_imgs") ||
      card.querySelector(".slide_imgs-swiper")
    );
  }

  function getSwiperFromCard(card) {
    var el = card.querySelector(".swiper, .swiper-initialized");
    if (!el) return null;
    var sw = el.swiper || el._swiperInstance || null;
    return sw && !sw.destroyed ? sw : null;
  }

  function processSwiperQueue() {
    if (!swiperInitQueue.length) {
      swiperQueueRunning = false;
      return;
    }
    swiperQueueRunning = true;
    var card = swiperInitQueue.shift();
    try {
      _doInitCardSwiper(card);
    } catch (e) {
      // audit M15b — one failed card must never jam the whole init pipeline
      // (an uncaught throw here stopped the rAF chain and left swiperQueueRunning
      // stuck on true — killing hover after sort/filter/pagination until reload).
    }
    requestAnimationFrame(processSwiperQueue);
  }

  function scheduleCardSwiper(card) {
    if (ACTIVE.has(card) || card.dataset.swiperInited === "1") return;
    if (swiperInitQueue.indexOf(card) !== -1) return;
    swiperInitQueue.push(card);
    if (!swiperQueueRunning) {
      requestAnimationFrame(processSwiperQueue);
    }
  }

  function _doInitCardSwiper(card) {
    if (ACTIVE.has(card) || card.dataset.swiperInited === "1") return;

    var container = getContainer(card);
    if (!container) return;

    var swatch = card.querySelector(".color_product_main.active_color");

    if (card.dataset.needsSwiperInit === "1" || !getSwiperFromCard(card)) {
      if (typeof initializeSwiper === "function") {
        initializeSwiper(container, swatch, card);
      }
      card.dataset.needsSwiperInit = "0";
    }

    var sw = getSwiperFromCard(card);
    if (sw) {
      ACTIVE.set(card, sw);
      card.dataset.swiperInited = "1";

      if (isDesktopHover() && !card.dataset.hoverBound) {
        card.dataset.hoverBound = "1";
        card.addEventListener("pointerenter", function () {
          var c = getSwiperFromCard(card);
          if (c && !c.destroyed) c.slideTo(1, 0);
        });
        card.addEventListener("pointerleave", function () {
          var c = getSwiperFromCard(card);
          if (c && !c.destroyed) c.slideTo(0, 0);
        });
      }
    }
  }

  function destroyCardSwiper(card) {
    var sw = ACTIVE.get(card);
    if (!sw) return;
    try {
      sw.destroy(true, true);
    } catch (_) {}
    try {
      var container = getContainer(card);
      if (container) container._swiperInstance = null;
    } catch (_) {}
    ACTIVE.delete(card);
    card.dataset.swiperInited = "0";
  }

  window.__CollectionInitCardSwiper = _doInitCardSwiper;
  window.__CollectionDestroyCardSwiper = destroyCardSwiper;

  // ----------------------------
  // INTERSECTION OBSERVER — init Swipers as cards approach viewport
  // ----------------------------
  var io = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          scheduleCardSwiper(entry.target);
        } else {
          destroyCardSwiper(entry.target);
        }
      });
    },
    { root: null, threshold: 0.01, rootMargin: "250px 0px 250px 0px" }
  );

  function observeAllCards(cards) {
    var list =
      cards && cards.length
        ? cards
        : document.querySelectorAll("#product-grid .card-wrapper");

    list.forEach(function (card) {
      if (!card.dataset.ioBound) {
        card.dataset.ioBound = "1";
        io.observe(card);
      }
    });
  }

  // Expose for facets.js and pagination-collection.liquid
  window.observeAllCards = observeAllCards;
  observeAllCards();

  // Eagerly init Swipers for cards already in/near the viewport on first paint
  setTimeout(function () {
    var buffer = 250;
    document
      .querySelectorAll("#product-grid .card-wrapper")
      .forEach(function (card) {
        var r = card.getBoundingClientRect();
        if (r.top < window.innerHeight + buffer && r.bottom > -buffer) {
          scheduleCardSwiper(card);
        }
      });
  }, 0);

  // ----------------------------
  // RE-INITIALIZE PRODUCT UI ON AJAX SWAP
  // (used by facets.js after filter changes; also re-runs on the
  // pagination-collection swap event for safety)
  // ----------------------------
  function reinitializeProductUI(newItems) {
    if (!newItems || !newItems.length) return;

    var swaps = [];
    for (var i = 0; i < newItems.length; i++) {
      var card = newItems[i];
      var container =
        card.querySelector(".slide_imgs") ||
        card.querySelector(".slide_imgs-swiper");
      var swatch = card.querySelector(".color_product_main.active_color");
      var selectedImages = swatch
        ? swatch.querySelector(
            ".selected-color-images .selcted_slide_imgs-wrapper"
          )
        : null;
      if (container && selectedImages) {
        swaps.push({ container: container, html: selectedImages.innerHTML, card: card });
      }
    }

    for (var j = 0; j < swaps.length; j++) {
      swaps[j].container.innerHTML = swaps[j].html;
      swaps[j].card.dataset.needsSwiperInit = "1";
    }

    if (
      window.LazyLoadInstance &&
      typeof window.LazyLoadInstance.update === "function"
    ) {
      window.LazyLoadInstance.update();
    }
  }
  window.__CollectionReinitProductUI = reinitializeProductUI;

  // After pagination swaps content, re-observe newly inserted cards.
  document.addEventListener("collection:loadmore:appended", function () {
    var grid = getProductGrid();
    if (!grid) return;
    var cards = grid.querySelectorAll(".card-wrapper");
    reinitializeProductUI(Array.from(grid.querySelectorAll(".grid__item")));
    observeAllCards(cards);
    updateCounter();
  });
});
